import React, { useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { cn } from '@/lib/utils';
import { getTweetItemAuthor, getTweetMediaImage } from '@/lib/searchResultPreview';

interface TweetMiniPreviewProps {
  item: Item;
}

/**
 * Small tweet preview for tight spaces (pinned strip): avatar + handle,
 * a few lines of text, and the first media image when there is one.
 * Works on partial rows (no title/url, missing author fields).
 */
export function TweetMiniPreview({ item }: TweetMiniPreviewProps) {
  const content = (item.content || {}) as Record<string, unknown>;
  const author = getTweetItemAuthor(item);
  const text = typeof content.text === 'string' ? content.text : '';
  const mediaImage = getTweetMediaImage(item);
  const [avatarError, setAvatarError] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const showMedia = !!mediaImage && !mediaError;
  const handle = author.username ? `@${author.username}` : author.name || 'Post on X';
  const initial = (author.name || author.username || '?')[0]?.toUpperCase();

  return (
    <div className="w-full h-full flex flex-col bg-card dark:bg-[#16181C] overflow-hidden" data-testid="tweet-mini-preview">
      {showMedia && (
        <img
          src={mediaImage}
          alt=""
          className="w-full h-1/2 object-cover flex-shrink-0"
          onError={() => setMediaError(true)}
        />
      )}
      <div className="flex items-center gap-1.5 px-2.5 pt-2 min-w-0">
        {author.profile_image_url && !avatarError ? (
          <img
            src={author.profile_image_url}
            alt={author.name || ''}
            className="w-5 h-5 rounded-full flex-shrink-0"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-muted-foreground">{initial}</span>
          </div>
        )}
        <span className="text-xs font-semibold text-foreground truncate">{handle}</span>
        <svg className="w-3 h-3 ml-auto flex-shrink-0 text-foreground/60" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </div>
      {text && (
        <p
          className={cn(
            'px-2.5 pt-1 text-xs leading-snug text-foreground/90 whitespace-pre-wrap break-words',
            showMedia ? 'line-clamp-2' : 'line-clamp-5'
          )}
        >
          {text}
        </p>
      )}
    </div>
  );
}
